/**
 * AuditReport.tsx — SPEC_05 Site Audit Report Component
 *
 * Pure presentation component that consumes an audit report JSON and renders:
 * - Score gauge (radial 0–100, color-coded)
 * - Three category cards (Crawlability / On-page / GEO)
 * - Top issues panel (errors first, max 10 visible)
 * - Site-wide findings panel
 * - Per-page findings table (expandable rows)
 * - Audit metadata footer
 *
 * Decoupled from authed state — accepts the audit object as a prop.
 * SPEC_14 can reuse this without coupling to authed module state.
 */
import React, { useState } from 'react';
import { AlertCircle, AlertTriangle, Info, ChevronDown, ChevronRight } from 'lucide-react';
import { GlassCard, GlassButton } from '../ui';
import { SeoAuditRecord, SeoAuditFinding, SeoAuditPageReport, MigrationCheckResult } from '../../services/api';
import MigrationCheckPanel from './MigrationCheckPanel';
import './AuditReport.css';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AuditReportProps {
    audit: SeoAuditRecord;
    showSignupCta?: boolean;
    onReaudit?: () => void;
    migrationCheck?: MigrationCheckResult | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getScoreClass(score: number): string {
    if (score >= 80) return 'excellent';
    if (score >= 60) return 'good';
    if (score >= 40) return 'fair';
    return 'poor';
}

function getBarPercent(value: number, max: number): number {
    return max > 0 ? Math.round((value / max) * 100) : 0;
}

function formatDate(dateStr: string | null): string {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString();
}

// ── Score Gauge (inline SVG) ──────────────────────────────────────────────────

const ScoreGauge: React.FC<{ score: number }> = ({ score }) => {
    const r = 54;
    const circumference = 2 * Math.PI * r;
    const filled = (score / 100) * circumference;
    const cls = getScoreClass(score);

    return (
        <div className="sa-audit-gauge">
            <svg width="140" height="140" viewBox="0 0 120 120">
                <circle
                    className="sa-audit-gauge-track"
                    cx="60" cy="60" r={r}
                />
                <circle
                    className={`sa-audit-gauge-fill ${cls}`}
                    cx="60" cy="60" r={r}
                    strokeDasharray={`${filled} ${circumference}`}
                    strokeDashoffset={0}
                />
            </svg>
            <div className="sa-audit-gauge-text">
                <span className="sa-audit-gauge-score">{score}</span>
                <span className="sa-audit-gauge-label">/ 100</span>
            </div>
        </div>
    );
};

// ── Category card ─────────────────────────────────────────────────────────────

interface CategoryCardProps {
    name: string;
    score: number;
    max: number;
}

const CategoryCard: React.FC<CategoryCardProps> = ({ name, score, max }) => {
    const pct = getBarPercent(score, max);
    const cls = getScoreClass((score / max) * 100);
    return (
        <div className="sa-audit-category-card">
            <div className="sa-audit-category-name">{name}</div>
            <div className="sa-audit-category-score">
                {score}
                <span className="sa-audit-category-max"> / {max}</span>
            </div>
            <div className="sa-audit-category-bar-track">
                <div
                    className={`sa-audit-category-bar-fill ${cls}`}
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    );
};

// ── Severity icon ─────────────────────────────────────────────────────────────

const SeverityIcon: React.FC<{ severity: string }> = ({ severity }) => {
    if (severity === 'error')   return <span className="sa-audit-severity-icon error">  <AlertCircle   size={14} /></span>;
    if (severity === 'warning') return <span className="sa-audit-severity-icon warning"><AlertTriangle size={14} /></span>;
    return                              <span className="sa-audit-severity-icon info">   <Info          size={14} /></span>;
};

// ── Finding row ───────────────────────────────────────────────────────────────

const FindingRow: React.FC<{ finding: SeoAuditFinding }> = ({ finding }) => (
    <div className="sa-audit-finding-row">
        <SeverityIcon severity={finding.severity} />
        <div className="sa-audit-finding-text">
            <div className="sa-audit-finding-check">{finding.check}</div>
            <div className="sa-audit-finding-message">{finding.message}</div>
            <div className="sa-audit-finding-category">{finding.category}</div>
        </div>
    </div>
);

// ── Page row (expandable) ─────────────────────────────────────────────────────

const PageRow: React.FC<{ page: SeoAuditPageReport }> = ({ page }) => {
    const [expanded, setExpanded] = useState(false);
    const errors   = page.findings.filter(f => f.severity === 'error').length;
    const warnings = page.findings.filter(f => f.severity === 'warning').length;
    const countCls = errors > 0 ? 'has-errors' : warnings > 0 ? 'has-warnings' : 'all-ok';
    const countText = errors > 0 ? `${errors} error${errors !== 1 ? 's' : ''}` :
                      warnings > 0 ? `${warnings} warning${warnings !== 1 ? 's' : ''}` : 'OK';

    return (
        <div className="sa-audit-page-row">
            <div className="sa-audit-page-header" onClick={() => setExpanded(e => !e)}>
                {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <span className="sa-audit-page-url" title={page.url}>{page.url}</span>
                <div className="sa-audit-page-meta">
                    <span>HTTP {page.status_code}</span>
                    {page.ttfb_ms > 0 && <span>{page.ttfb_ms} ms</span>}
                    {page.page_weight_kb > 0 && <span>{page.page_weight_kb} KB</span>}
                    <span className={`sa-audit-page-finding-count ${countCls}`}>{countText}</span>
                </div>
            </div>
            {expanded && page.findings.length > 0 && (
                <div className="sa-audit-page-body">
                    {page.findings.map((f, i) => <FindingRow key={i} finding={f} />)}
                </div>
            )}
            {expanded && page.findings.length === 0 && (
                <div className="sa-audit-page-body" style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                    No issues found on this page.
                </div>
            )}
        </div>
    );
};

// ── Main AuditReport component ────────────────────────────────────────────────

const AuditReport: React.FC<AuditReportProps> = ({ audit, showSignupCta = false, onReaudit, migrationCheck }) => {
    const [showAllIssues, setShowAllIssues] = useState(false);
    const report = audit.report_json;

    if (audit.status === 'failed') {
        return (
            <GlassCard>
                <div className="sa-audit-failed">
                    Audit failed: {audit.error_message || 'Unknown error'}
                    {onReaudit && (
                        <div style={{ marginTop: 12 }}>
                            <GlassButton variant="secondary" onClick={onReaudit}>Re-audit</GlassButton>
                        </div>
                    )}
                </div>
            </GlassCard>
        );
    }

    if (!report || audit.status !== 'complete') {
        return null;
    }

    // Gather all findings across all pages + site
    const allFindings: SeoAuditFinding[] = [
        ...report.site_findings,
        ...report.pages.flatMap(p => p.findings),
    ];
    const errorFindings   = allFindings.filter(f => f.severity === 'error');
    const warningFindings = allFindings.filter(f => f.severity === 'warning');
    const topIssues = [...errorFindings, ...warningFindings].slice(0, showAllIssues ? undefined : 10);
    const totalIssues = errorFindings.length + warningFindings.length;

    const scoreMax = report.score.max || { crawlability: 30, onpage: 40, geo: 30 };

    return (
        <div className="sa-audit-report">
            {/* Score summary */}
            <GlassCard>
                <div className="sa-audit-score-section">
                    <ScoreGauge score={report.score.total} />
                    <div className="sa-audit-category-cards">
                        <CategoryCard name="Crawlability" score={report.score.crawlability} max={scoreMax.crawlability} />
                        <CategoryCard name="On-page SEO"  score={report.score.onpage}       max={scoreMax.onpage} />
                        <CategoryCard name="GEO Readiness" score={report.score.geo}         max={scoreMax.geo} />
                    </div>
                </div>

                {/* Actions */}
                <div className="sa-audit-actions" style={{ padding: '0 24px 20px' }}>
                    {onReaudit && (
                        <GlassButton variant="secondary" onClick={onReaudit}>Re-audit</GlassButton>
                    )}
                </div>
            </GlassCard>

            {/* Site-wide findings */}
            {report.site_findings.length > 0 && (
                <GlassCard>
                    <h3 style={{ margin: '0 0 12px', fontSize: '0.9rem', color: 'var(--text)' }}>
                        Site-wide Findings
                    </h3>
                    <div className="sa-audit-site-findings">
                        {report.site_findings.map((f, i) => <FindingRow key={i} finding={f} />)}
                    </div>
                </GlassCard>
            )}

            {/* Top issues */}
            {totalIssues > 0 && (
                <GlassCard>
                    <h3 style={{ margin: '0 0 12px', fontSize: '0.9rem', color: 'var(--text)' }}>
                        Top Issues ({totalIssues})
                    </h3>
                    <div className="sa-audit-top-issues">
                        {topIssues.map((f, i) => <FindingRow key={i} finding={f} />)}
                    </div>
                    {!showAllIssues && totalIssues > 10 && (
                        <div className="sa-audit-show-more">
                            <GlassButton variant="ghost" onClick={() => setShowAllIssues(true)}>
                                Show all {totalIssues} issues
                            </GlassButton>
                        </div>
                    )}
                </GlassCard>
            )}

            {/* Migration Check panel — only when has_gsc_history is true */}
            {migrationCheck && (
                <GlassCard>
                    <MigrationCheckPanel result={migrationCheck} />
                </GlassCard>
            )}

            {/* Per-page findings */}
            {report.pages.length > 0 && (
                <GlassCard>
                    <h3 style={{ margin: '0 0 12px', fontSize: '0.9rem', color: 'var(--text)' }}>
                        Audited Pages ({report.pages.length})
                    </h3>
                    <div className="sa-audit-pages-list">
                        {report.pages.map((page, i) => <PageRow key={i} page={page} />)}
                    </div>
                </GlassCard>
            )}

            {/* Metadata footer */}
            <GlassCard>
                <div className="sa-audit-meta">
                    <div className="sa-audit-meta-item">
                        <span className="sa-audit-meta-label">Root URL</span>
                        <span className="sa-audit-meta-value">{audit.root_url}</span>
                    </div>
                    <div className="sa-audit-meta-item">
                        <span className="sa-audit-meta-label">Pages audited</span>
                        <span className="sa-audit-meta-value">{report.pages.length} / {audit.max_pages} max</span>
                    </div>
                    <div className="sa-audit-meta-item">
                        <span className="sa-audit-meta-label">Run at</span>
                        <span className="sa-audit-meta-value">{formatDate(audit.created_at)}</span>
                    </div>
                    <div className="sa-audit-meta-item">
                        <span className="sa-audit-meta-label">Completed</span>
                        <span className="sa-audit-meta-value">{formatDate(audit.completed_at)}</span>
                    </div>
                    <div className="sa-audit-meta-item">
                        <span className="sa-audit-meta-label">Audit ID</span>
                        <span className="sa-audit-meta-value" style={{ fontFamily: 'monospace', fontSize: '0.65rem' }}>{audit.audit_id}</span>
                    </div>
                </div>
            </GlassCard>

            {/* Signup CTA (SPEC_14 public surface) */}
            {showSignupCta && (
                <GlassCard>
                    <div className="sa-audit-signup-cta">
                        <h3>Fix every issue with AIME</h3>
                        <p>Sign up free and run unlimited audits, get AI-powered fix suggestions, and monitor your SEO health over time.</p>
                        <GlassButton variant="primary" onClick={() => window.location.href = '/register'}>
                            Get started free
                        </GlassButton>
                    </div>
                </GlassCard>
            )}
        </div>
    );
};

export default AuditReport;
